import { useMemo } from 'react'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useSearchAvailableAreaCodes } from '@forwardflow/queries/onboarding.queries'
import { areaCodeOf } from '../utils/format'
import { fullName } from '../utils/seats'
import { MAX_AREA_CODES } from '../utils/constants'

export interface IAreaSuggestion {
   code: string
   /** Whose phone it came from. */
   name: string
   isOwner: boolean
   /** Whether numbers can be bought in it right now; undefined while checking. */
   available: boolean | undefined
   isChecking: boolean
}

/**
 * The area codes a customer can pick from: their own mobile first, then the
 * team's, each checked for availability against the number inventory. The
 * leads themselves are still syncing from Follow Up Boss at this point, so
 * the team's phones are the honest signal for "local".
 */
export const useAreaCodeSuggestions = () => {
   const { onboardingData } = useOnboardingStoreV2()
   const teamCodes = useMemo(() => {
      const users = [...(onboardingData.invitedUsers || [])].sort(
         (a, b) => Number(b.isOwner) - Number(a.isOwner)
      )
      const seen = new Set<string>()
      const out: { code: string; name: string; isOwner: boolean }[] = []
      users.forEach((u) => {
         const code = areaCodeOf(u.phone)
         if (!code || seen.has(code)) return
         seen.add(code)
         out.push({ code, name: fullName(u), isOwner: Boolean(u.isOwner) })
      })
      return out.slice(0, MAX_AREA_CODES)
   }, [onboardingData.invitedUsers])

   // One availability check per slot, so the hooks stay stable.
   const check0 = useSearchAvailableAreaCodes(teamCodes[0]?.code || '')
   const check1 = useSearchAvailableAreaCodes(teamCodes[1]?.code || '')
   const check2 = useSearchAvailableAreaCodes(teamCodes[2]?.code || '')
   const checks = [check0, check1, check2]
   const suggestions: IAreaSuggestion[] = teamCodes.map((t, i) => {
      const list: unknown = checks[i].data?.areaCodes
      const available = Array.isArray(list)
         ? list.map(String).includes(t.code)
         : undefined
      return { ...t, available, isChecking: checks[i].isLoading }
   })
   const isSuggesting = suggestions.some((s) => s.isChecking)
   const ready = suggestions
      .filter((s) => s.available === true)
      .map((s) => s.code)
   const ownerCode = suggestions.find((s) => s.isOwner)?.code
   return { suggestions, isSuggesting, ready, ownerCode }
}
